import React, { useState } from 'react'

import { useBulkAction } from '@/hooks/useBulkAction'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { Box, Modal, MultilineTextInput, Text } from '@hivewatch/dark'

import {
  CreateBulkIncidentActionInput,
  IncidentActionType,
} from '@/graphql/generated/schemas'
import { BUTTON_PRESS, startTimer, trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

// Direct file import, like the legacy BulkActionModal's ResolveToggle: the
// incident-drawer barrels would drag drawer-only modules into the table.
import { DarkResolveToggle } from '../../incident-drawer/resolve/components/DarkResolveToggle'

const NOTE_MAX_LENGTH = 1_000

// Preserves the exact legacy Mixpanel event name fired on submit.
const BULK_RESOLVE_ANALYTIC_NAME = `${BUTTON_PRESS} Bulk Resolve - Resolve`

interface DarkBulkActionModalIProps {
  incidentIds: string[]
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

/**
 * Rebrand variant of {@link ./BulkActionModal.BulkActionModal}, built on the
 * `@hivewatch/dark` `Modal`. Rendered from the rebranded incidents table
 * (`DarkIncidentsTable`); the legacy table keeps the legacy modal.
 */
export const DarkBulkActionModal = ({
  incidentIds,
  isOpen,
  onClose,
  onSuccess,
}: DarkBulkActionModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { showSuccess, showError } = useToasts({
    colorScheme,
    isRebrand: true,
  })
  const { submitBulkAction, isSubmitting } = useBulkAction()

  const [note, setNote] = useState('')
  const [isReal, setIsReal] = useState(true)

  const isLight = colorScheme === 'light'
  const primaryColor = isLight ? 'light.text.primary' : 'text.primary'
  const secondaryColor = isLight ? 'light.text.secondary' : 'text.secondary'

  const resetForm = () => {
    setNote('')
    setIsReal(true)
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetForm()
    onClose()
  }

  const handleResolve = async () => {
    if (incidentIds.length === 0) {
      showError(t('incidents:bulk_resolve_no_incidents'))
      return
    }

    startTimer(BULK_RESOLVE_ANALYTIC_NAME)

    const params: CreateBulkIncidentActionInput = {
      message: note,
      types: [
        IncidentActionType.Resolved,
        isReal
          ? IncidentActionType.SetRealToTrue
          : IncidentActionType.SetRealToFalse,
      ],
      incidentIds,
    }

    // Only the mutation is guarded: once it has resolved the incidents, a
    // throw from the follow-up side effects must not report a failure and
    // invite a second submit.
    try {
      await submitBulkAction(params)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'bulk_resolve',
          feature: 'incidents_table',
          surface: 'incidents_table_action_bar',
        },
        extra: { incidentCount: incidentIds.length, isReal },
      })
      showError(t('incidents:bulk_resolve_error'))
      return
    }

    // Clear the selection and close first, so a throw from the toast or
    // analytics below can't leave the committed resolve open to resubmit.
    resetForm()
    onSuccess()
    onClose()

    try {
      showSuccess(
        tVar(t, 'incidents:bulk_resolve_success', { count: incidentIds.length })
      )
      trackEvent(BULK_RESOLVE_ANALYTIC_NAME, {
        incidentCount: incidentIds.length,
        real: isReal,
        message: note,
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'bulk_resolve_side_effects',
          feature: 'incidents_table',
          surface: 'incidents_table_action_bar',
        },
      })
    }
  }

  return (
    <Modal
      cancelButtonLabel={t('common:cancel')}
      // The in-progress note would be lost on an accidental Escape or
      // overlay click.
      closeOnEsc={false}
      closeOnOverlayClick={false}
      colorScheme={colorScheme}
      header={
        <Text color={secondaryColor} textStyle='body'>
          {t('incidents:bulk_resolve_subtitle')}
        </Text>
      }
      isOpen={isOpen}
      isSaveLoading={isSubmitting}
      maxW='560px'
      onClose={handleClose}
      onSave={handleResolve}
      saveButtonLabel={t('incidents:resolve')}
      showCancelButton={true}
      size='compact'
      title={t('incidents:bulk_resolve')}
    >
      <Box display='flex' flexDirection='column' gap='16px'>
        <MultilineTextInput
          colorScheme={colorScheme}
          data-testid='bulk-resolve-note'
          // The labeled wrapper's side border rails read as stray lines
          // inside a modal; drop them and their inset padding.
          hasSideBorders={false}
          isDisabled={isSubmitting}
          label={t('incidents:bulk_resolve_note_label')}
          maxLength={NOTE_MAX_LENGTH}
          onChange={(event) => setNote(event.target.value)}
          outerPadding='0'
          placeholder={t('incidents:bulk_resolve_note_placeholder')}
          value={note}
        />

        <Box display='flex' flexDirection='column' gap='8px'>
          <Text color={primaryColor} textStyle='body-bold'>
            {t('incidents:bulk_resolve_real_question')}
          </Text>
          <Text color={secondaryColor} fontStyle='italic' textStyle='caption'>
            {t('incidents:bulk_resolve_false_note')}
          </Text>
          <DarkResolveToggle
            colorScheme={colorScheme}
            isLoading={isSubmitting}
            isReal={isReal}
            onChange={setIsReal}
          />
        </Box>
      </Box>
    </Modal>
  )
}
